/* eslint-disable no-param-reassign */
import {
  createEntityAdapter,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";
import { Area, RectLayout } from "./GeometricLayout";
import { ScrollAction } from "./actions";

export type EditorType = "textarea" | "contenteditable";

type OuterHTML = string;
type TextAreaValue = string;

export interface AttachedEditor {
  id: number;
  mirrorStyle: { [property: string]: string };
  boundingClientRect: RectLayout;

  /** The OuterHTML string if it's a rich editor, or the value property if it's a textarea */
  content: OuterHTML | TextAreaValue;

  scrollTop: number;
  scrollLeft: number;

  // Size of the editor including
  // content hidden from overflow
  scrollHeight: number;
  scrollWidth: number;

  border: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };

  padding: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };

  type: EditorType;

  /**
   * Width and height of the text content visible on the screen, plus padding
   */
  contentArea: Area;

  isLoading: boolean;

  /**
   * The document id that the grammar engine uses to keep continuity for edits.
   *
   * If null, we haven't sent a check yet. The grammar engine fills this in on its subsequent responses.
   */
  docId: string | null;
}

export const editorAdapter = createEntityAdapter<AttachedEditor>({
  selectId: (editor) => editor.id,
});

const initialState = {
  ...editorAdapter.getInitialState(),
  active: null as number | null,
};

export const editorsSlice = createSlice({
  name: "Editors",
  initialState,
  reducers: {
    attach(state, action: PayloadAction<AttachedEditor>) {
      const { payload } = action;
      editorAdapter.addOne(state, payload);
      state.active = payload.id;
    },
    detach(state, action: PayloadAction<number>) {
      const { payload: id } = action;
      editorAdapter.removeOne(state, id);
      if (state.active === id) {
        state.active = null;
      }
    },
    reflow: editorAdapter.updateOne,
    scrollAreaChange(
      state,
      action: PayloadAction<{
        id: number;
        scrollHeight: number;
        scrollWidth: number;
      }>
    ) {
      const { id, scrollHeight, scrollWidth } = action.payload;
      editorAdapter.updateOne(state, {
        id,
        changes: {
          scrollHeight,
          scrollWidth,
        },
      });
    },
    change(state, action: PayloadAction<{ id: number; content: string }>) {
      const { id, content } = action.payload;

      // Change means that the editor has changed values somehow, which is a good indication
      // that this is the active editor
      state.active = id;
      editorAdapter.updateOne(state, {
        id,
        changes: {
          content,
        },
      });
    },
    scroll(state, action: ScrollAction) {
      const { editorId: id, scrollLeft, scrollTop } = action.payload;
      editorAdapter.updateOne(state, {
        id,
        changes: {
          scrollLeft,
          scrollTop,
        },
      });
    },
    windowScroll: editorAdapter.updateOne,
    loading(
      state,
      action: PayloadAction<{ editorId: number; isLoading: boolean }>
    ) {
      const { editorId, isLoading } = action.payload;
      editorAdapter.updateOne(state, {
        id: editorId,
        changes: {
          isLoading,
        },
      });
    },
    updateDocId(
      state,
      action: PayloadAction<{ editorId: number; docId: string }>
    ) {
      const { editorId, docId } = action.payload;
      const editor = state.entities[editorId];
      if (editor) {
        editor.docId = docId;
      } else {
        console.warn(
          "Trying to set docId",
          docId,
          "on editor that does not exist:",
          editorId
        );
      }
    },
  },
});

export const {
  attach,
  detach,
  reflow,
  change,
  scroll,
  loading,
  scrollAreaChange,
  updateDocId,
} = editorsSlice.actions;

export default editorsSlice.reducer;
